import React, {
  forwardRef,
  PropsWithChildren,
  useCallback,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { IdentifyOptions, UnifiedAuthEvents } from "../analytics";
import { useAnalytics } from "../analytics/context";
import { identifySendGridUser } from "../analytics/identifySendGridUser";
import { websiteContext } from "../context";
import { useExternalNavigation } from "../external-navigate";
import { useExternalNavigate } from "../hooks/useExternalNavigate";
import {
  AppState,
  extractUrlParameters,
  IdPAuthContext,
  IdPAuthContextInterface,
  IdPAuthProvider,
  IdPErrorCode,
  IdPIssuerConfig,
  IdPRedirectError,
  IdPUser,
  isSilentLoginFallbackError,
  LoginWithIdPOptions,
  useHeldTokensOrSilentLogin,
  useIdPAuth,
} from "../idp-auth";
import { setMovedParam } from "../movedQueryParams";
import {
  SendGridRouterProvider,
  SendGridSessionProvider,
  useSendGridRouter,
  useSendGridSession,
} from "../sendgrid-auth";
import { AccountRegion } from "../types/regionalApi";
import { LOGOUT_REDIRECT_TO, UnifiedProvisionError, UnifiedProvisionPlanError } from "./constants";
import {
  BasicAuthLoginOptions,
  ConfirmAccountLinkingResult,
  initialUnifiedAuthState,
  LogoutOptions,
  NavigateOptions,
  RedirectLoginOptions,
  UnifiedAccountLinkType,
  UnifiedAuthContext,
  UnifiedAuthContextInterface,
  UnifiedProvisionEligibilityResult,
} from "./context";
import { bannerStatus, loginError } from "./error";
import {
  deleteAuthenticateToken,
  fetchAuthenticateToken,
  fetchIdentifier,
  fetchSessionToken,
  fetchSSO,
  FetchSSOResponse,
  fetchStatus,
  fetchTwoFASetting,
  fetchUnifiedProvision,
  fetchUnifiedProvisionPlan,
  fetchUnifiedSearch,
  FetchUnifiedSearchResponse,
  fetchUnifiedSignup,
} from "./fetch";
import { useUnifiedUserInfoCookie } from "./hooks";
import {
  appStateToInteractiveLoginOptions,
  navigateToRedirectLoginOptions,
  redirectLoginToIdPOptions,
} from "./loginOptions";
import { reducer } from "./reducer";
import { getSessionUsername, removeSessionUsername, setSessionUsername } from "./sessionUsername";
import { acceptTeammateInvite, makeSureUserCanLink } from "./utils";

/**
 * UseAuthenticateOptions uses authenticate options
 */
export interface UnifiedAuthProviderOptions {
  children: React.ReactNode;
  /**
   * The IdP plug to read. Any IdPAuthContextInterface implementation works, which is what tests
   * use to stand in for the IdP.
   */
  idpContext?: React.Context<IdPAuthContextInterface>;
  context?: React.Context<UnifiedAuthContextInterface>;

  // IdP tenant info
  domain: string;
  clientId: string;
  audience: string;

  // Proxy Domain info for Authorize call
  proxyDomainForAuthorize?: string;

  // Issuers trusted after the Auth0 tenant (VITE_IDP_ADDITIONAL_ISSUERS); none when deployed.
  additionalIssuers?: IdPIssuerConfig[];

  // Unified Account Linking configurations
  enableUnifiedAccountLinking?: boolean;
  // Unified account Switching
  enableUnifiedAccountSwitching?: boolean;
}

export type { AppState } from "../idp-auth";

function preserveInviteTokenForRetry(token: string | undefined): void {
  if (!token) return;
  try {
    setMovedParam("token", token);
  } catch (e) {
    console.error("Failed to preserve invite token for retry", e);
  }
}

/**
 * This is the actual provider, we split between this and the impl because we want to test by passing context map.
 * @param children
 * @param props
 * @constructor
 */
export const UnifiedAuthProvider = ({
  children,
  ...props
}: PropsWithChildren<UnifiedAuthProviderOptions>) => {
  const { domain, clientId, audience, proxyDomainForAuthorize, additionalIssuers } = props;
  const ref = useRef<UnifiedAuthContextInterface>(null);

  const onRedirectCallback = (appState?: AppState, user?: IdPUser) => {
    (async () => {
      if (ref.current !== null) {
        //
        await ref.current.handleRedirectCallback(appState, user);
      }
    })();
  };
  const onRedirectError = (error: IdPRedirectError) => {
    (async () => {
      if (ref.current !== null) {
        await ref.current.handleRedirectError(error);
      } else {
        console.error(
          "An IdP callback error arrived before the unified auth provider mounted",
          error
        );
      }
    })();
  };
  // Analytics comes from the AnalyticsProvider above this one (see main.tsx).
  return (
    <IdPAuthProvider
      domain={domain}
      clientId={clientId}
      audience={audience}
      proxyDomainForAuthorize={proxyDomainForAuthorize}
      additionalIssuers={additionalIssuers}
      onRedirectCallback={onRedirectCallback}
      onRedirectError={onRedirectError}>
      <SendGridSessionProvider>
        <SendGridRouterProvider>
          <UnifiedAuthProviderImpl ref={ref} {...props}>
            {children}
          </UnifiedAuthProviderImpl>
        </SendGridRouterProvider>
      </SendGridSessionProvider>
    </IdPAuthProvider>
  );
};

/**
 * We don't export this because it keeps the united logic of SendGrid's approach to log-in/logout and the IdP's unified
 * log-in/logout.
 */
export const UnifiedAuthProviderImpl = forwardRef(
  (opts: UnifiedAuthProviderOptions, ref): JSX.Element => {
    const {
      children,
      idpContext = IdPAuthContext,
      context = UnifiedAuthContext,
      enableUnifiedAccountLinking = false,
    } = opts;
    // Without an AnalyticsProvider above, this is NoopAnalytics, which makes testing easier.
    const analytics = useAnalytics();
    const {
      loginWithRedirect: idpLoginWithRedirect,
      getHeldTokens,
      logout: logoutFromIdP,
      isAuthenticated: isAuthenticatedWithIdP,
      isLoading: isLoadingWithIdP,
      user: idpUser,
    } = useIdPAuth(idpContext);
    const {
      makoToken,
      accountRegion,
      setSession,
      clearSession,
      isAuthenticated: isAuthenticatedWithSendGrid,
      navigationOptions,
    } = useSendGridSession();
    const { navigate: navigateToMako } = useSendGridRouter();
    const externalNavigate = useExternalNavigate();
    const reactRouterNavigate = useNavigate();
    const [state, dispatch] = useReducer(reducer, initialUnifiedAuthState);
    const { setUnifiedUserInfo, removeUnifiedUserInfo } = useUnifiedUserInfoCookie();
    const { store } = useExternalNavigation(websiteContext);
    // Set when an IdP callback on this page load failed, so no further silent login can loop.
    const idpCallbackFailed = useRef(false);

    const setUnifiedUserInformationInCookie = useCallback(
      (user: IdPUser | undefined) => {
        const { given_name = "", family_name = "", email = "" } = user ?? {};
        setUnifiedUserInfo({
          given_name,
          family_name,
          email,
        });
      },
      [setUnifiedUserInfo]
    );

    /**
     * This starts an IdP login. It clears the SendGrid session first, unless the login comes from
     * account linking or switching, which keep the Mako token.
     */
    const loginWithIdP = useCallback(
      async (opts: LoginWithIdPOptions = {}) => {
        const { email = "", fromUnifiedAccountLink, fromUnifiedAccountSwitching } = opts;

        if (!fromUnifiedAccountLink && !fromUnifiedAccountSwitching) {
          // prevent removal of mako token when coming from unified account linking
          clearSession();
        }

        // The user didn't start a silent login, so keep it out of the login funnel.
        if (!opts.silent) {
          await analytics.track(UnifiedAuthEvents.Auth0LoginStart(email));
        }

        return idpLoginWithRedirect(opts);
      },
      [idpLoginWithRedirect, clearSession, analytics]
    );

    const fetchUnifiedAccountLinkingEligibility = useCallback(
      async (accountRegion: AccountRegion): Promise<UnifiedProvisionEligibilityResult> => {
        const response = await fetchUnifiedProvisionPlan(makoToken!, accountRegion);
        return makeSureUserCanLink(response);
      },
      [makoToken]
    );

    /**
     * This redirects user to the correct login page based on the login type.
     */
    const redirectLogin = useCallback(
      async (opts: RedirectLoginOptions) => {
        const { loginType, username = "" } = opts;
        if (loginType === "mp-sso") {
          // Check username (email) against SSO if it doesn't belong to SSO, redirect to /login
          let response = await fetchSSO(username);
          let data: FetchSSOResponse = await response.json();
          if (response.status === 422 || response.status === 404) {
            dispatch({ type: "ERROR", error: loginError(bannerStatus.SSONotSetUpError.banner) });
            return;
          }
          if (response.status === 500) {
            const errorMsg = "Our system is currently offline. Please try again in a few minutes.";
            // Signal sciences is currently killing the http1.1 connection instead of letting it complete
            // So even when they send us a 406 allegedly it's actually coming back as a network error in jquery terms it's
            // a status code 0
            dispatch({ type: "ERROR", error: loginError(errorMsg) });
            return;
          }
          dispatch({
            type: "SSO_LOGIN_COMPLETE",
            ssoLoginInfo: {
              idp_url: data.idp_url,
              saml_request: data.saml_request,
              relay_state: data.relay_state,
            },
          });
          return;
        } else if (loginType === "cidp-oidc") {
          await loginWithIdP(redirectLoginToIdPOptions(opts));
          dispatch({ type: "NAVIGATE_COMPLETE" });
          return;
        } else {
          setSessionUsername(username);
          reactRouterNavigate("/login/password");
          dispatch({ type: "NAVIGATE_COMPLETE" });
          return;
        }
      },
      [dispatch, loginWithIdP, reactRouterNavigate]
    );

    const loginWithBasicAuth = useCallback(
      async (opts: BasicAuthLoginOptions = { username: "", password: "" }) => {
        const { username, password, bot_detection_token } = opts;
        dispatch({ type: "LOGIN_WITH_IDENTIFIER_AND_PASSWORD_STARTED" });
        const authenticateResult = await fetchAuthenticateToken(
          username,
          password,
          bot_detection_token
        );
        if (!authenticateResult.success) {
          // if it's a sso or unified login redirect
          if (authenticateResult.redirect) {
            await redirectLogin({ loginType: authenticateResult.auth_type ?? "", username });
            return;
          }
          await analytics.track(UnifiedAuthEvents.FailedLoginAttemptBasic(username));
          removeSessionUsername();
          dispatch({ type: "ERROR", error: loginError(authenticateResult.reason) });
          return;
        }

        const token = authenticateResult.token;
        const setup2FARequired = authenticateResult.setup_2fa_required;
        const passwordResetRequired = authenticateResult.password_reset_required;
        const accountRegion = authenticateResult.region;
        let twoFA = await fetchTwoFASetting(token, accountRegion);
        if (!twoFA.success) {
          removeSessionUsername();
          dispatch({ type: "ERROR", error: loginError(twoFA.reason) });
          return;
        }
        let mfaSettingIsVerified = twoFA.is_verified;
        setSession({
          token,
          accountRegion,
          setup2FARequired,
          passwordResetRequired,
          mfaSettingIsVerified,
        });
        await identifySendGridUser(analytics, {
          makoAuthToken: token,
          accountRegion,
          identity: { email: username },
        });
        await analytics.track(UnifiedAuthEvents.BasicAuthLoginComplete(username));
        // False indicates it's not a unified (IdP) login
        await analytics.track(UnifiedAuthEvents.SendGridLoginComplete(false, undefined, username));
        const navigationOptions = {
          setup2FARequired,
          passwordResetRequired,
          mfaSettingIsVerified,
          skipSecurityCheck: mfaSettingIsVerified,
          redirectToOAuth: false,
          needsOnboarding: false,
        };
        if (token && enableUnifiedAccountLinking) {
          const response = await fetchUnifiedProvisionPlan(token, accountRegion);
          if (
            !response.eligible &&
            response.reason === UnifiedProvisionPlanError.VALIDATE_2FA_REQUIRED
          ) {
            setSessionUsername(username);
            externalNavigate.toValidate2FAForUnifiedAccountLinking();
            return;
          }
          const unifiedAccountLinkingEligibility = makeSureUserCanLink(response);

          if (unifiedAccountLinkingEligibility.eligible) {
            reactRouterNavigate("/unified_account_linking/prompt");
            return;
          }
        }
        removeSessionUsername();
        navigateToMako(navigationOptions);
        dispatch({ type: "LOGIN_WITH_IDENTIFIER_AND_PASSWORD_COMPLETE" });
      },
      [
        analytics,
        setSession,
        enableUnifiedAccountLinking,
        navigateToMako,
        externalNavigate,
        reactRouterNavigate,
        redirectLogin,
      ]
    );

    /**
     * This logs a user out of all the various parts for SendGrid, it clears the session, ends the IdP session
     * Removes the session username and removes the unified user info.
     */
    const logout = useCallback(
      async (opts: LogoutOptions) => {
        const {
          returnTo = "",
          sendgridOnly = false,
          twilioOnly = false,
          noRedirect = false,
        } = opts;
        let logoutFromSendgrid = true;
        let logoutFromTwilio = true;
        if (sendgridOnly || twilioOnly) {
          logoutFromTwilio = !sendgridOnly;
          logoutFromSendgrid = !twilioOnly;
        }
        if (returnTo) {
          store.set(LOGOUT_REDIRECT_TO, returnTo);
        }
        analytics.reset();
        if (logoutFromSendgrid) {
          if (makoToken && opts.region) {
            await deleteAuthenticateToken(makoToken, opts.region);
          }
          clearSession();
          removeSessionUsername();
        }
        if (logoutFromTwilio) {
          let openUrl: boolean | ((url: string) => void);
          if (noRedirect) {
            openUrl = false;
          } else {
            openUrl = (url: string) => {
              window.location.replace(url);
            };
          }
          removeUnifiedUserInfo();
          await logoutFromIdP({
            openUrl: openUrl,
            returnTo: window.location.origin + `/logged-out`,
          });
        }
      },
      [analytics, store, makoToken, clearSession, logoutFromIdP, removeUnifiedUserInfo]
    );

    // This function will try and fetch the session token from an access token
    const fetchTokenAndRedirect = useCallback(
      async (
        access_token: string,
        // default to the signed-in IdP user
        user: IdPUser | undefined = idpUser,
        opts?: {
          // used for multi account access to select a specific Sendgrid UserId
          userId?: string;
          // if true, call fetchStatus to set needsOnboarding
          checkOnboarding?: boolean;
          // When set, IPAM and banned paths redirect to errorPage instead of the legacy /login/identifier.
          // No token write-back: IPAM and banned are treated as terminal for the reseller flow (user must
          // start over). IPAM is an environmental condition (IP can be whitelisted later) but is treated
          // as terminal here for simplicity — the network issue must be resolved before a retry is possible.
          // MAAT similarly skips write-back; the account switcher takes over, but the invited account may
          // not appear there if the accept failed for a non-already-linked reason (see limitation note below).
          resellerFallThrough?: { errorPage: string };
        }
      ): Promise<"session" | "ipam_blocked" | "banned" | "maat" | "no_session"> => {
        const result = opts?.userId
          ? await fetchSessionToken(access_token, opts?.userId)
          : await fetchSessionToken(access_token);
        if (!result.success) {
          const blockedErrorPage = opts?.resellerFallThrough?.errorPage ?? "/login/identifier";

          // Check if the error requires redirection
          if (
            result.reason &&
            typeof result.reason === "string" &&
            result.reason.includes("The requestor's IP Address is not whitelisted")
          ) {
            reactRouterNavigate(`${blockedErrorPage}?error=ERR_IP_ADDRESS_NOT_ALLOWED`);
            return "ipam_blocked";
          }
          // check if we need to select a userID for MAAT
          if (
            result.reason &&
            typeof result.reason === "string" &&
            result.reason.includes("user ID not specified for multi-account access teammate")
          ) {
            reactRouterNavigate(`/unified_account_switching/accounts`);
            // this is considered success, the accounts switcher takes over
            return "maat";
          }
          // check if the user is banned
          if (
            result.reason &&
            typeof result.reason === "string" &&
            result.reason.includes(
              "You are not authorized to access SendGrid. Please contact Support"
            )
          ) {
            reactRouterNavigate(`${blockedErrorPage}?error=ERR_USER_FORBIDDEN_ACCESS`);
            return "banned";
          }
          if (result.reason) {
            dispatch({ type: "ERROR", error: loginError(result.reason) });
            // Return "no_session" so tryRedirectingUser continues: the reseller guard handles
            // write-back and navigation; non-reseller callers fall through to fetchUnifiedSearch.
            return "no_session";
          }
          console.error("fetchTokenAndRedirect: session failed with no reason", {
            success: result.success,
          });
          return "no_session";
        }
        // The user has a valid IdP access token and fetch session found them
        // navigate to mako.
        setUnifiedUserInformationInCookie(user);
        const token = result.token ?? "";
        const passwordResetRequired = result.password_reset_required ?? false;
        const setup2FARequired = result.setup_2fa_required ?? false;
        const accountRegion = result.region;

        let needsOnboarding = false;
        if (opts?.checkOnboarding) {
          needsOnboarding = await fetchStatus(token, accountRegion);
        }

        let identity: IdentifyOptions = {};
        if (user?.email) {
          identity.email = user.email;
        }
        if (user?.["https://twilio.com/unified_user_id"]) {
          identity.traits = {
            ...identity.traits,
            unifiedUserId: user?.["https://twilio.com/unified_user_id"],
          };
        }
        await identifySendGridUser(analytics, { makoAuthToken: token, accountRegion, identity });
        await analytics.track(UnifiedAuthEvents.Auth0LoginComplete(user));
        // True indicates it's a unified (IdP) login
        await analytics.track(UnifiedAuthEvents.SendGridLoginComplete(true, user));

        setSession({
          token,
          accountRegion,
          setup2FARequired,
          passwordResetRequired,
        });
        navigateToMako({
          setup2FARequired,
          passwordResetRequired,
          mfaSettingIsVerified: false,
          skipSecurityCheck: true,
          redirectToOAuth: false,
          needsOnboarding,
        });
        return "session";
      },
      [
        analytics,
        reactRouterNavigate,
        setUnifiedUserInformationInCookie,
        setSession,
        navigateToMako,
        idpUser,
      ]
    );

    const signupUser = useCallback(
      async (access_token: string, id_token: string, appState?: AppState, user?: IdPUser) => {
        // If we are coming from unified signup then we can bypass the signup.sendgrid.com and just create the user.
        const bypassSignup = !!appState && appState.screenHint === "signup";

        // Track signup completion before any navigation/redirect since both paths will navigate away
        // and we want to ensure the event is sent.
        // This is important for analytics to capture the signup event.
        // If bypassSignup is true, we are not redirecting to the signup page and go straight to mako.
        // If bypassSignup is false, we are redirecting to the signup page.
        // pull all query parameters from the URL
        const params = extractUrlParameters(new URLSearchParams(window.location.search));
        await analytics.track(UnifiedAuthEvents.SendGridSignupComplete(bypassSignup, params, user));

        if (bypassSignup) {
          const signupResponse = await fetchUnifiedSignup(access_token, id_token);
          if (!signupResponse || !signupResponse.ok) {
            throw loginError("Error creating unified user please contact support");
          }

          // `checkOnboarding` is set to true here to fetch the S2S in order to decide whether to show the onboarding
          // page or not
          await fetchTokenAndRedirect(access_token, user, { checkOnboarding: true });
        } else {
          // Regular signup flow - redirect to external signup page
          setUnifiedUserInformationInCookie(user);
          externalNavigate.toUnifiedSignup();
        }
      },
      [analytics, setUnifiedUserInformationInCookie, externalNavigate, fetchTokenAndRedirect]
    );

    const tryRedirectingUser = useCallback(
      async (access_token: string, id_token: string, appState?: AppState, user?: IdPUser) => {
        // Set when a reseller accept was rejected as already-satisfied and we continued to sign-in
        // anyway. Carried out of the catch so that, if sign-in then fails to produce a session, we
        // can conclude the already-has-access premise was wrong and stop rather than falling into
        // the login-or-signup ladder below. Holds the error id so the reseller page can report what
        // chaos actually said.
        let resellerFallThroughErrorId: string | undefined;

        try {
          await acceptTeammateInvite(access_token, id_token, appState);
        } catch (error) {
          const fromReseller = Boolean(appState?.["fromReseller"]);
          const errorPage = fromReseller ? "/unified_login/identifier" : "/login/identifier";

          // Parse the error id up front so we can both route on it and decide whether preserving the
          // token is useful.
          const parsedErrorId =
            error instanceof Error ? error.message.split(":")[1]?.trim() : undefined;
          const isInvalidToken = parsedErrorId === "ERR_TEAMMATE_INVITE_INVALID_TOKEN";

          // A reseller resubscribe can carry an invite for an account this identity already has
          // access to. Chaos rejects that accept because its account-scoped teammate check matches
          // on credential name alone, before the multi-account-aware check that would have allowed
          // reusing the existing credential. In that case there is nothing left to link, so continue
          // to sign-in instead of failing it: /v3/sessions and the account switcher already handle a
          // multi-account identity, which is the same path a direct sign-in takes.
          //
          // Scoped to the reseller flow on purpose: the long-standing legacy invite path treats
          // this error as terminal and is deliberately left unchanged.
          //
          // This error id does NOT by itself mean the identity already has access. Chaos raises it
          // from several checks, including one reached only after it has established that no
          // credential with this email exists — the inverse of already-having-access. Chaos's
          // pre-Auth0 verify-email step screens most of those, but it queries different sources than
          // the accept does and runs before a minutes-long Auth0 round trip, so it is not a
          // guarantee. Rather than trust the error id, we continue to sign-in. If /v3/sessions
          // returns a 401 with no reason, or any other session error not recognized as IPAM/banned/MAAT
          // (those return "no_session"; IPAM/banned/MAAT return their own outcome strings and have
          // already navigated), the resellerFallThroughErrorId guard below stops before
          // fetchUnifiedSearch and restores the terminal-with-retry behaviour. IPAM and
          // banned outcomes are redirected to the reseller error page via the resellerFallThrough
          // option; MAAT routes to the account switcher unconditionally (that path is already
          // multi-account-aware). Note: MAAT only signals that the identity holds multiple credentials
          // — it does not confirm the invited account is among them. If the accept failed for a
          // non-already-linked reason the invited account will not appear in the switcher.
          // Without that guard this fall-through could reach signupUser and create a
          // standalone account for an invite that was never accepted (the INC-18719 shape).
          const resellerAlreadyLinked =
            fromReseller && parsedErrorId === "ERR_TEAMMATE_INVITE_USERNAME_NOT_AVAILABLE";

          if (!resellerAlreadyLinked) {
            // The reseller flow must land back on its own page (/unified_login/identifier), not the
            // legacy sign-in page. The invite token was consumed on the way to the IdP but is still
            // server-valid for a transient failure, so write it back to sessionStorage; the reseller
            // page peeks it to offer a retry. Skip the write-back for an invalid/expired token — the
            // page shows a terminal "start over" state and never retries, so a dead token would only
            // linger at rest. preserveInviteTokenForRetry wraps the write in a try/catch so a
            // sessionStorage failure can't skip the navigate below. (CLRF-2268 Bug #1/#2)
            // Note: on a transient failure the still-valid token intentionally persists at rest
            // (same-origin, short-lived) until the retry consumes it or the tab closes; the M1
            // freshness gate keeps it from attaching to an unrelated same-tab login. (review S2)
            if (fromReseller && !isInvalidToken) {
              preserveInviteTokenForRetry(appState?.token);
            }

            if (error instanceof Error) {
              const errorMessage = error.message;
              const [errorStatus, errorId, errorDescription] = errorMessage.split(":");

              reactRouterNavigate(
                `${errorPage}?error=${errorId?.trim() || "ERR_TEAMMATE_INVITE_INTERNAL"}`
              );

              console.error(`Error: ${errorStatus} - ${errorId} - ${errorDescription}`);
            } else {
              reactRouterNavigate(`${errorPage}?error=ERR_TEAMMATE_INVITE_INTERNAL`);
              console.error("An unknown error occurred", error);
            }
            return;
          }

          resellerFallThroughErrorId = parsedErrorId;
          const errorMsg = error instanceof Error ? error.message : String(error);
          console.error(
            `Reseller accept rejected (${errorMsg}); premise not confirmed — continuing to sign-in to verify access`
          );
        }

        const fromUnifiedAccountLink: boolean = Boolean(appState?.["fromUnifiedAccountLink"]);
        if (fromUnifiedAccountLink) {
          reactRouterNavigate("/unified_account_linking/confirm");
          return;
        }

        const fromUnifiedAccountSwitcher: boolean = Boolean(
          appState?.["fromUnifiedAccountSwitching"]
        );
        if (fromUnifiedAccountSwitcher) {
          reactRouterNavigate("/unified_account_switching/accounts");
          return;
        }

        let fetchResult: "session" | "ipam_blocked" | "banned" | "maat" | "no_session" =
          "no_session";
        try {
          fetchResult = await fetchTokenAndRedirect(
            access_token,
            user,
            resellerFallThroughErrorId
              ? { resellerFallThrough: { errorPage: "/unified_login/identifier" } }
              : undefined
          );
        } catch (e) {
          if (!resellerFallThroughErrorId) throw e;
          // A network or parse error during sign-in is transient: the accept was already rejected
          // so the server-side state is unchanged and the token is still valid for a retry.
          console.error("fetchTokenAndRedirect threw during reseller fall-through", e);
          analytics
            .track(
              UnifiedAuthEvents.ResellerFallThrough(
                resellerFallThroughErrorId,
                "retry",
                !!appState?.token
              )
            )
            .catch(console.error);
          preserveInviteTokenForRetry(appState?.token);
          // encodeURIComponent intentionally omitted: value is always ERR_[A-Z_]+ from the
          // trusted chaos backend and contains no URL-special characters.
          reactRouterNavigate(`/unified_login/identifier?error=${resellerFallThroughErrorId}`);
          return;
        }
        if (fetchResult === "session") {
          if (resellerFallThroughErrorId) {
            analytics
              .track(
                UnifiedAuthEvents.ResellerFallThrough(
                  resellerFallThroughErrorId,
                  "signed_in",
                  !!appState?.token
                )
              )
              .catch(console.error);
          }
          return;
        }
        if (fetchResult !== "no_session") {
          // "ipam_blocked", "banned", or "maat" — fetchTokenAndRedirect already navigated.
          if (resellerFallThroughErrorId) {
            analytics
              .track(
                UnifiedAuthEvents.ResellerFallThrough(
                  resellerFallThroughErrorId,
                  fetchResult,
                  !!appState?.token
                )
              )
              .catch(console.error);
          }
          return;
        }

        if (resellerFallThroughErrorId) {
          analytics
            .track(
              UnifiedAuthEvents.ResellerFallThrough(
                resellerFallThroughErrorId,
                "retry",
                !!appState?.token
              )
            )
            .catch(console.error);
          preserveInviteTokenForRetry(appState?.token);
          // encodeURIComponent intentionally omitted: value is always ERR_[A-Z_]+ from the
          // trusted chaos backend and contains no URL-special characters.
          reactRouterNavigate(`/unified_login/identifier?error=${resellerFallThroughErrorId}`);
          return;
        }

        // Use unified search response to find out if the user actually exists.
        const unifiedSearchResponse = await fetchUnifiedSearch(access_token, id_token);
        if (!unifiedSearchResponse.ok) {
          throw loginError("Error retrieving unified user please contact support");
        }

        // If the user is not found navigate to the signup flow.
        if (unifiedSearchResponse.status === 404) {
          await signupUser(access_token, id_token, appState, user);
          return;
        }

        const response: FetchUnifiedSearchResponse = await unifiedSearchResponse.json();
        const userEmailExists = Array.isArray(response.result) && response.result.length > 0;
        if (userEmailExists) {
          const email = user?.email || "";
          reactRouterNavigate(
            `/login/password?error=ERR_TEAMMATE_EXISTS&email=${encodeURIComponent(email)}`
          );
          return;
        }
        await signupUser(access_token, id_token, appState, user);
        return;
      },
      [reactRouterNavigate, fetchTokenAndRedirect, signupUser, analytics]
    );

    const handleRedirectCallback = useCallback(
      async (appState?: AppState, user?: IdPUser) => {
        dispatch({ type: "HANDLE_REDIRECT_CALLBACK_STARTED" });
        // do not remove unified user info if we are coming from unified account switching
        // user is able to Go Back without changing sessions from this page
        if (!appState?.["fromUnifiedAccountSwitching"]) {
          removeUnifiedUserInfo();
        }

        try {
          // The IdP client has just exchanged the code, so the tokens are held.
          const tokens = await getHeldTokens();
          if (!tokens) {
            throw new Error("No IdP tokens are held after the callback");
          }
          const { access_token, id_token } = tokens;
          await tryRedirectingUser(access_token, id_token, appState, user);
          dispatch({ type: "HANDLE_REDIRECT_CALLBACK_COMPLETE" });
        } catch (error: unknown) {
          dispatch({ type: "ERROR", error: loginError(error) });
          console.error(`Error: ${error}`);
        }
      },
      [getHeldTokens, tryRedirectingUser, removeUnifiedUserInfo]
    );

    /**
     * Handles a callback the IdP client could not complete. A silent attempt that needs the user
     * to see the IdP falls back to an interactive login; anything else is an error.
     */
    const handleRedirectError = useCallback(
      async (redirectError: IdPRedirectError) => {
        idpCallbackFailed.current = true;
        const { error, errorDescription, appState } = redirectError;
        if (isSilentLoginFallbackError(error) && appState?.silent) {
          try {
            await loginWithIdP(appStateToInteractiveLoginOptions(appState));
            return;
          } catch (loginFailure: unknown) {
            console.error("Falling back to an interactive IdP login failed", loginFailure);
          }
        }
        console.error("IdP callback failed", error, errorDescription);
        // A non-OAuth failure's message is for the logs, not the error page.
        const message = error === IdPErrorCode.UNKNOWN_ERROR ? error : errorDescription || error;
        dispatch({ type: "ERROR", error: loginError(message) });
      },
      [loginWithIdP]
    );

    useImperativeHandle(
      ref,
      () => {
        return {
          handleRedirectCallback: async (appState?: AppState, user?: IdPUser) => {
            await handleRedirectCallback(appState, user);
          },
          handleRedirectError: async (error: IdPRedirectError) => {
            await handleRedirectError(error);
          },
        };
      },
      [handleRedirectCallback, handleRedirectError]
    );

    const navigate = useCallback(
      async (opts: NavigateOptions) => {
        const { identifier: username, bot_detection_token = "" } = opts;
        dispatch({ type: "NAVIGATE_STARTED" });
        try {
          // Routing for SSO, the IdP, and Basic Auth
          // Build the request for the gandalf identifier endpoint
          // Call the gandalf identifier endpoint
          const result = await fetchIdentifier(username, bot_detection_token);

          // Clear error parameters after making the request, regardless of the response
          const currentUrl = new URL(window.location.href);
          const hasErrorParams =
            currentUrl.searchParams.has("error") ||
            currentUrl.searchParams.has("error_description");

          if (!result.success && result.reason === "ERR_USERNAME_NOT_FOUND") {
            // Set new error parameter, replacing any existing ones
            reactRouterNavigate("/login/identifier?error=ERR_USERNAME_NOT_FOUND", {
              replace: hasErrorParams,
            });
            dispatch({ type: "NAVIGATE_COMPLETE" });
            return;
          }

          // For all other responses, clear error parameters if they exist
          if (hasErrorParams) {
            reactRouterNavigate(window.location.pathname, { replace: true });
          }

          if (!result.success && result.reason) {
            dispatch({ type: "ERROR", error: loginError(result.reason) });
            return;
          }
          if (!result.success) {
            setSessionUsername(username);
            reactRouterNavigate("/login/password");
            dispatch({ type: "NAVIGATE_COMPLETE" });
            return;
          }

          await redirectLogin(navigateToRedirectLoginOptions(result.login_type, opts));
        } catch (error: unknown) {
          dispatch({ type: "ERROR", error: loginError(error) });
        }
      },
      [reactRouterNavigate, redirectLogin]
    );

    const redirectToUnifiedAuthWithLinking = useCallback(
      async (linkType: UnifiedAccountLinkType, prefillEmail?: string) => {
        await loginWithIdP({
          screen_hint: linkType,
          fromUnifiedAccountLink: true,
          email: prefillEmail,
        });
      },
      [loginWithIdP]
    );

    const skipUnifiedAccountLinking = useCallback(async () => {
      if (!isAuthenticatedWithSendGrid) {
        return;
      }
      if (accountRegion) {
        await identifySendGridUser(analytics, {
          makoAuthToken: makoToken,
          accountRegion,
          identity: { email: getSessionUsername() },
        });
      }
      removeSessionUsername();
      navigateToMako({
        ...navigationOptions,
        mfaSettingIsVerified: false,
        skipSecurityCheck: navigationOptions.mfaSettingIsVerified,
        redirectToOAuth: false,
      });
    }, [
      analytics,
      accountRegion,
      isAuthenticatedWithSendGrid,
      makoToken,
      navigateToMako,
      navigationOptions,
    ]);

    const goToLoginIfNotAuthenticatedWithSendgrid = useCallback(() => {
      if (!isAuthenticatedWithSendGrid) {
        reactRouterNavigate("/login");
      }
    }, [isAuthenticatedWithSendGrid, reactRouterNavigate]);

    const mayStartSilentLogin = useCallback(() => !idpCallbackFailed.current, []);

    const getHeldTokensOrSilentLogin = useHeldTokensOrSilentLogin(
      getHeldTokens,
      loginWithIdP,
      mayStartSilentLogin
    );

    const confirmUnifiedAccountLinking = useCallback(
      async (accountRegion: AccountRegion): Promise<ConfirmAccountLinkingResult> => {
        if (!makoToken) {
          return { success: false, error: UnifiedProvisionError.GENERAL_ERROR };
        }
        try {
          // With no tokens held (e.g. after a page reload), this starts a silent login that comes
          // back to the confirm page.
          const result = await getHeldTokensOrSilentLogin({ fromUnifiedAccountLink: true });
          if (result.status === "redirecting") {
            return { success: false, redirecting: true };
          }
          if (result.status === "unavailable") {
            return { success: false, error: UnifiedProvisionError.IDP_ERROR };
          }
          if (!idpUser) {
            return { success: false, error: UnifiedProvisionError.GENERAL_ERROR };
          }
          const { access_token, id_token } = result.tokens;

          const response = await fetchUnifiedProvision(makoToken, id_token, accountRegion);
          if (!response.success) {
            return { success: false, error: response.reason };
          }

          await fetchTokenAndRedirect(access_token, idpUser);
          return { success: true };
        } catch (error) {
          return { success: false, error: UnifiedProvisionError.GENERAL_ERROR };
        }
      },
      [idpUser, fetchTokenAndRedirect, getHeldTokensOrSilentLogin, makoToken]
    );

    const isLoading = useMemo(
      () => state.isLoadingWithSendGrid || isLoadingWithIdP,
      [state, isLoadingWithIdP]
    );

    const contextValue = useMemo(() => {
      return {
        ...state,
        isAuthenticatedWithSendGrid,
        isAuthenticatedWithIdP,
        isLoadingWithIdP,
        isLoading,
        navigate,
        loginWithIdP,
        loginWithBasicAuth,
        handleRedirectCallback,
        handleRedirectError,
        logout,
        redirectToUnifiedAuthWithLinking,
        skipUnifiedAccountLinking,
        getHeldTokensOrSilentLogin,
        goToLoginIfNotAuthenticatedWithSendgrid,
        confirmUnifiedAccountLinking,
        fetchTokenAndRedirect,
        fetchUnifiedAccountLinkingEligibility,
        signupUser,
      };
    }, [
      state,
      isAuthenticatedWithSendGrid,
      isAuthenticatedWithIdP,
      isLoadingWithIdP,
      isLoading,
      navigate,
      loginWithIdP,
      loginWithBasicAuth,
      handleRedirectCallback,
      handleRedirectError,
      logout,
      redirectToUnifiedAuthWithLinking,
      skipUnifiedAccountLinking,
      getHeldTokensOrSilentLogin,
      goToLoginIfNotAuthenticatedWithSendgrid,
      confirmUnifiedAccountLinking,
      fetchTokenAndRedirect,
      fetchUnifiedAccountLinkingEligibility,
      signupUser,
    ]);
    return <context.Provider value={contextValue}>{children}</context.Provider>;
  }
);
